import { CaptureOptions } from 'posthog-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMount, useUnmount } from 'react-use';

import {
  Button,
  Icon,
  IconName,
  Stack,
  Text,
  useToast,
} from '@eluve/components';

import { usePreventAutoReloadOnNewVersion as usePreventAutoReload } from './usePreventAutoReloadOnNewVersion';

export type CountdownReloadToastEventNames = {
  viewed: string;
  userDismissed: string;
  forceDismissed: string;
  userReloaded: string;
  autoReloaded: string;
};

export type CountdownReloadToastProps = {
  toastId: string;
  title: string;
  description: string;
  iconName?: IconName;
  countdownSeconds?: number;
  onDismiss: (forced: boolean) => void;
  onReload?: (auto: boolean) => void;
  eventNames: CountdownReloadToastEventNames;
  captureEvent: (
    event: string,
    meta?: Record<string, unknown>,
    options?: CaptureOptions,
  ) => void;
  meta?: Record<string, unknown>;
};

export const CountdownReloadToast: React.FC<CountdownReloadToastProps> = ({
  toastId,
  title,
  description,
  iconName = 'RefreshCw',
  countdownSeconds = 15,
  onDismiss,
  onReload,
  eventNames,
  captureEvent,
  meta,
}) => {
  const [reloadingCountdown, setReloadingCountdown] =
    useState(countdownSeconds);
  const intervalIdRef = useRef<number | undefined>(undefined);
  const toast = useToast();

  const handleDismiss = useCallback(
    (forced: boolean) => {
      captureEvent(
        forced ? eventNames.forceDismissed : eventNames.userDismissed,
      );
      onDismiss(forced);
      toast.dismiss(toastId);
    },
    [captureEvent, eventNames, onDismiss, toast, toastId],
  );

  const startCountdown = () => {
    intervalIdRef.current = setInterval(() => {
      const shouldPreventAutoReload = usePreventAutoReload
        .getState()
        .shouldPrevent();
      if (shouldPreventAutoReload) {
        handleDismiss(true);
        return;
      }
      setReloadingCountdown((countdown) => countdown - 1);
    }, 1000) as unknown as number;
  };

  const stopCountdown = () => {
    clearInterval(intervalIdRef.current);
  };

  useMount(() => {
    captureEvent(eventNames.viewed, meta);
  });

  const reload = useCallback(
    (auto: boolean) => {
      const shouldPreventAutoReload = usePreventAutoReload
        .getState()
        .shouldPrevent();
      if (shouldPreventAutoReload) {
        handleDismiss(true);
        return;
      }

      captureEvent(
        auto ? eventNames.autoReloaded : eventNames.userReloaded,
        undefined,
        { send_instantly: true },
      );

      onReload?.(auto);

      setTimeout(() => {
        window.location.reload();
      }, 50);
    },
    [captureEvent, eventNames, handleDismiss, onReload],
  );

  useEffect(() => {
    if (reloadingCountdown === 0) {
      reload(true);
    }
  }, [reload, reloadingCountdown]);

  useMount(() => {
    startCountdown();
  });

  useUnmount(() => {
    stopCountdown();
  });

  return (
    <Stack
      align="start"
      gap={3}
      onMouseEnter={stopCountdown}
      onMouseLeave={startCountdown}
      direction="horizontal"
    >
      <Icon name={iconName} size="lg" />
      <Stack>
        <Text weight="semibold">{title}</Text>
        <Text className="text-balance" weight="regular">
          {description}
        </Text>
        <Stack className="mt-3 mb-1" gap={2} direction="horizontal">
          <Button
            size="sm"
            onClick={() => reload(false)}
          >{`Reload Now (${reloadingCountdown})`}</Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDismiss(false)}
          >
            Dismiss
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
